import { useState, useEffect } from 'react';
import './Board.css';
import Piece from './Piece';
import PromotionDialog from './PromotionDialog';
import { GameState, Move, PieceType, Piece as PieceType2 } from '../../engine/types';
import { generateLegalMoves, makeMove } from '../../engine/moves';

interface BoardProps {
  gameState: GameState;
  onMove: (newState: GameState, move: Move) => void;
  lastMove?: Move | null;
  disabled?: boolean;
  flipped?: boolean;
  hintMove?: Move | null;
  pieceSet?: string; // Piece set identifier to trigger re-renders
}

interface AnimatingMove {
  from: number;
  to: number;
  piece: PieceType2;
  // For castling, we need to animate the rook as well
  castlingRookFrom?: number;
  castlingRookTo?: number;
}

/**
 * Board component - renders an 8x8 chessboard with coordinates and pieces
 */
export function Board({ gameState, onMove, lastMove = null, disabled = false, flipped = false, hintMove = null, pieceSet = 'classic' }: BoardProps) {
  const [selectedSquare, setSelectedSquare] = useState<number | null>(null);
  const [legalMoves, setLegalMoves] = useState<Move[]>([]);
  const [draggedSquare, setDraggedSquare] = useState<number | null>(null);
  const [promotionMoves, setPromotionMoves] = useState<Move[] | null>(null);
  const [animatingMove, setAnimatingMove] = useState<AnimatingMove | null>(null);
  
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const ranks = ['1', '2', '3', '4', '5', '6', '7', '8'];
  
  /**
   * Execute a move, checking if it requires promotion selection
   */
  const executeMove = (move: Move, fromDrag: boolean = false) => {
    // Check if this is a promotion move
    if (move.promotion !== undefined) {
      // Get all promotion options for this move
      const promotionOptions = legalMoves.filter(
        m => m.from === move.from && m.to === move.to && m.promotion !== undefined
      );
      
      if (promotionOptions.length > 0) {
        // Show promotion dialog
        setPromotionMoves(promotionOptions);
        return;
      }
    }
    
    // If this is a drag move, skip animation (already animated by drag)
    if (fromDrag) {
      const newState = makeMove(gameState, move);
      onMove(newState, move);
      
      // Clear selection
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }
    
    // Setup animation for click moves
    const piece = gameState.board[move.from];
    if (piece) {
      const animData: AnimatingMove = {
        from: move.from,
        to: move.to,
        piece: piece,
      };
      
      // Check if this is castling - need to animate rook too
      if (move.isCastling) {
        // Determine rook position based on castling type
        const rank = move.from >> 3; // Get rank from king position
        const kingFile = move.from & 7;
        const targetFile = move.to & 7;
        
        if (targetFile > kingFile) {
          // Kingside castling - rook moves from h-file to f-file
          animData.castlingRookFrom = rank * 8 + 7;
          animData.castlingRookTo = rank * 8 + 5;
        } else {
          // Queenside castling - rook moves from a-file to d-file
          animData.castlingRookFrom = rank * 8 + 0;
          animData.castlingRookTo = rank * 8 + 3;
        }
      }
      
      setAnimatingMove(animData);
      
      // Execute move after short delay to allow animation setup
      setTimeout(() => {
        const newState = makeMove(gameState, move);
        onMove(newState, move);
        
        // Clear selection
        setSelectedSquare(null);
        setLegalMoves([]);
      }, 10);
    }
  };
  
  /**
   * Clear animation state after animation completes
   */
  useEffect(() => {
    if (animatingMove) {
      const timer = setTimeout(() => {
        setAnimatingMove(null);
      }, 300); // Match CSS transition duration
      
      return () => clearTimeout(timer);
    }
  }, [animatingMove]);
  
  /**
   * Handle promotion piece selection
   */
  const handlePromotionSelect = (pieceType: PieceType) => {
    if (!promotionMoves) return;
    
    // Find the move with the selected promotion piece
    const selectedMove = promotionMoves.find(m => m.promotion === pieceType);
    
    if (selectedMove) {
      // Execute the promotion move
      const newState = makeMove(gameState, selectedMove);
      onMove(newState, selectedMove);
    }
    
    // Close promotion dialog and clear selection
    setPromotionMoves(null);
    setSelectedSquare(null);
    setLegalMoves([]);
  };
  
  /**
   * Handle drag start event
   */
  const handleDragStart = (squareIndex: number) => (e: React.DragEvent<HTMLDivElement>) => {
    // Don't allow dragging if game is over
    if (disabled) {
      e.preventDefault();
      return;
    }
    
    const piece = gameState.board[squareIndex];
    
    // Only allow dragging pieces of the active player
    if (piece && piece.color === gameState.activeColor) {
      setDraggedSquare(squareIndex);
      // Store square index in dataTransfer for later use
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', squareIndex.toString());
      
      // Calculate legal moves for this piece
      const allLegalMoves = generateLegalMoves(gameState);
      const pieceLegalMoves = allLegalMoves.filter(move => move.from === squareIndex);
      setLegalMoves(pieceLegalMoves);
      setSelectedSquare(squareIndex);
    } else {
      // Prevent dragging opponent's pieces or empty squares
      e.preventDefault();
    }
  };
  
  /**
   * Handle drag end event
   */
  const handleDragEnd = () => {
    // Clear all drag-related state
    // This handles both successful drops and cancelled drags
    setDraggedSquare(null);
    setSelectedSquare(null);
    setLegalMoves([]);
  };
  
  /**
   * Handle drag over event - required to allow dropping
   */
  const handleDragOver = (squareIndex: number) => (e: React.DragEvent<HTMLDivElement>) => {
    // Prevent default to enable drop
    e.preventDefault();
    
    // Set drop effect based on whether this is a legal target
    const isLegalTarget = legalMoves.some(move => move.to === squareIndex);
    if (isLegalTarget) {
      e.dataTransfer.dropEffect = 'move';
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };
  
  /**
   * Handle drop event - execute the move if legal
   */
  const handleDrop = (squareIndex: number) => (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    // Get source square from dataTransfer
    const sourceSquareStr = e.dataTransfer.getData('text/plain');
    const sourceSquare = parseInt(sourceSquareStr, 10);
    
    // Find matching legal move
    const targetMove = legalMoves.find(move => move.from === sourceSquare && move.to === squareIndex);
    
    if (targetMove) {
      // Execute the move (will handle promotion if needed)
      // Pass true to indicate this is a drag move (skip animation)
      executeMove(targetMove, true);
    }
    
    // State cleanup will be handled by handleDragEnd which fires after drop
  };
  
  /**
   * Handle square click event
   */
  const handleSquareClick = (squareIndex: number) => {
    // Don't allow clicks if game is over
    if (disabled) {
      return;
    }
    
    const piece = gameState.board[squareIndex];
    
    // Check if clicking on a legal move destination
    const targetMove = legalMoves.find(move => move.to === squareIndex);
    if (targetMove) {
      // Execute the move (will handle promotion if needed)
      executeMove(targetMove);
      return;
    }
    
    // If clicking the already selected square, deselect it
    if (selectedSquare === squareIndex) {
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }
    
    // If there's a piece on this square and it belongs to the active player
    if (piece && piece.color === gameState.activeColor) {
      setSelectedSquare(squareIndex);
      
      // Calculate legal moves for this piece
      const allLegalMoves = generateLegalMoves(gameState);
      const pieceLegalMoves = allLegalMoves.filter(move => move.from === squareIndex);
      setLegalMoves(pieceLegalMoves);
    } else {
      // Clicking empty square or opponent's piece deselects
      setSelectedSquare(null);
      setLegalMoves([]);
    }
  };
  
  // Generate 64 squares (8 ranks × 8 files)
  const squares = [];
  
  // Helper function to calculate pixel offset for animation
  const getSquareOffset = (fromSquare: number, toSquare: number): { x: number, y: number } => {
    const fromFile = fromSquare & 7;
    const fromRank = fromSquare >> 3;
    const toFile = toSquare & 7;
    const toRank = toSquare >> 3;
    
    // Calculate difference in grid units
    // When board is normal (white at bottom), board renders rank 7->0 top to bottom
    // When board is flipped (black at bottom), board renders rank 0->7 top to bottom
    const fileDiff = flipped ? (fromFile - toFile) : (toFile - fromFile);
    const rankDiff = flipped ? (toRank - fromRank) : (fromRank - toRank);
    
    // Each square is 12.5% of board width (100% / 8)
    return {
      x: fileDiff * 100,
      y: rankDiff * 100,
    };
  };
  
  // Determine iteration order based on flip state
  const rankStart = flipped ? 0 : 7;
  const rankEnd = flipped ? 7 : 0;
  const rankStep = flipped ? 1 : -1;
  const fileStart = flipped ? 7 : 0;
  const fileEnd = flipped ? 0 : 7;
  const fileStep = flipped ? -1 : 1;
  
  for (let rank = rankStart; flipped ? (rank <= rankEnd) : (rank >= rankEnd); rank += rankStep) {
    for (let file = fileStart; flipped ? (file >= fileEnd) : (file <= fileEnd); file += fileStep) {
      const squareIndex = rank * 8 + file;
      const isLight = (rank + file) % 2 === 0;
      const isSelected = selectedSquare === squareIndex;
      const isDragSource = draggedSquare === squareIndex;
      const isLastMoveFrom = lastMove && lastMove.from === squareIndex;
      const isLastMoveTo = lastMove && lastMove.to === squareIndex;
      
      // Check if this square is part of a hint
      const isHintFrom = hintMove && hintMove.from === squareIndex;
      const isHintTo = hintMove && hintMove.to === squareIndex;
      
      // Check if this square is part of an animation
      const isAnimatingFrom = animatingMove && (animatingMove.from === squareIndex || animatingMove.castlingRookFrom === squareIndex);
      
      // Check if this square is a legal move destination
      const legalMove = legalMoves.find(move => move.to === squareIndex);
      const isLegalMove = !!legalMove;
      const isLegalCapture = isLegalMove && !!legalMove.captured;
      
      // Build CSS class string
      let squareClass = isLight ? 'square light' : 'square dark';
      if (isSelected) {
        squareClass += ' selected';
      }
      if (isDragSource) {
        squareClass += ' drag-source';
      }
      if (isLastMoveFrom) {
        squareClass += ' last-move-from';
      }
      if (isLastMoveTo) {
        squareClass += ' last-move-to';
      }
      if (isHintFrom) {
        squareClass += ' hint-from';
      }
      if (isHintTo) {
        squareClass += ' hint-to';
      }
      if (isLegalMove && !isLegalCapture) {
        squareClass += ' legal-move';
      }
      if (isLegalCapture) {
        squareClass += ' legal-capture';
      }
      
      // Get piece at this square
      const piece = gameState.board[squareIndex];
      
      // Determine if piece is draggable (belongs to active player and game not over)
      const isDraggable = !disabled && !!(piece && piece.color === gameState.activeColor);
      const isPieceBeingDragged = isDragSource && !!piece;
      
      // Check if we should render an animating piece here
      let animatingPiece = null;
      let animationTransform = '';
      
      if (animatingMove && isAnimatingFrom) {
        // This is the source square - render animating piece
        let targetSquare: number;
        let animPiece;
        
        if (animatingMove.from === squareIndex) {
          // Main piece (king in castling, or any other piece)
          targetSquare = animatingMove.to;
          animPiece = animatingMove.piece;
        } else if (animatingMove.castlingRookFrom === squareIndex) {
          // Castling rook
          targetSquare = animatingMove.castlingRookTo!;
          animPiece = gameState.board[animatingMove.castlingRookFrom];
        }
        
        if (targetSquare! !== undefined && animPiece) {
          const offset = getSquareOffset(squareIndex, targetSquare);
          animationTransform = `translate(${offset.x}%, ${offset.y}%)`;
          animatingPiece = animPiece;
        }
      }
      
      // Add coordinate labels on edge squares
      // When flipped, top edge shows rank labels, bottom shows files
      // When normal, bottom shows files, left shows ranks
      const isBottomRank = flipped ? (rank === 7) : (rank === 0);
      const isLeftFile = flipped ? (file === 7) : (file === 0);
      
      squares.push(
        <div
          key={squareIndex}
          className={squareClass}
          data-square={squareIndex}
          onClick={() => handleSquareClick(squareIndex)}
          onDragOver={handleDragOver(squareIndex)}
          onDrop={handleDrop(squareIndex)}
        >
          {piece && !isAnimatingFrom && (
            <Piece 
              type={piece.type} 
              color={piece.color}
              draggable={isDraggable}
              dragging={isPieceBeingDragged}
              pieceSet={pieceSet}
              onDragStart={handleDragStart(squareIndex)}
              onDragEnd={handleDragEnd}
            />
          )}
          {animatingPiece && (
            <div 
              className="animating-piece"
              style={{ transform: animationTransform }}
            >
              <Piece 
                type={animatingPiece.type} 
                color={animatingPiece.color}
                pieceSet={pieceSet}
              />
            </div>
          )}
          {isBottomRank && (
            <span className="file-label">{files[file]}</span>
          )}
          {isLeftFile && (
            <span className="rank-label">{ranks[rank]}</span>
          )}
        </div>
      );
    }
  }
  
  return (
    <div className="board-container">
      <div className="board">
        {squares}
      </div>
      {promotionMoves && (
        <PromotionDialog
          color={gameState.activeColor}
          onSelect={handlePromotionSelect}
        />
      )}
    </div>
  );
}
